import { useState } from 'react';
import { FiArrowUpCircle, FiGift, FiRotateCcw, FiUserCheck, FiXCircle } from 'react-icons/fi';
import { toast } from 'sonner';

import { MANUALLY_GRANTED_MESSAGE, isManuallyGrantedPlan } from '@/common/error';
import { useUncancelSubscriptionMutation } from '@/store/subscriptions/subscriptions.store';

import { isLifetimePlan, isPaidPlan } from '../subscription.display';
import s from '../subscription.module.scss';
import { canTakeOverPlan } from '../subscription.takeover';
import type { ManagedVesselSubscription } from '../useManagedVesselSubscriptions';

import { CancelPlanModal } from './CancelPlanModal';
import { VesselPlanModal } from './VesselPlanModal';

/** Moves a vessel to another plan — opens the checkout form. */
function ChangePlanButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className={s.changePlanBtn} onClick={onClick}>
      <FiArrowUpCircle />
      <span>Change plan</span>
    </button>
  );
}

/**
 * Buys a plan out from under whoever is paying for it. Filled rather than outlined: it is the
 * only control such a vessel offers, so it is not competing with anything.
 */
function TakeOverPlanButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className={s.takeOverPlanBtn} onClick={onClick}>
      <FiUserCheck />
      <span>Take over plan</span>
    </button>
  );
}

/**
 * What a lifetime plan shows in place of the buttons. Not a button: the plan is never billed and
 * the vessel cannot change it, so there is nothing here to press — the row under the bill says why.
 */
function LifetimeBadge() {
  return (
    <span className={s.lifetimeBadge}>
      <FiGift />
      <span>Lifetime plan</span>
    </span>
  );
}

/** Schedules the end of a paid plan — quiet, and confirmed before anything is sent. */
function CancelPlanButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className={s.cancelPlanBtn} onClick={onClick}>
      <FiXCircle />
      <span>Cancel plan</span>
    </button>
  );
}

/** Clears a pending cancellation. Restorative, so it acts on the first click. */
function ResumePlanButton({ onClick, isPending }: { onClick: () => void; isPending: boolean }) {
  return (
    <button type="button" className={s.resumePlanBtn} onClick={onClick} disabled={isPending}>
      <FiRotateCcw />
      <span>{isPending ? 'Resuming…' : 'Resume plan'}</span>
    </button>
  );
}

/**
 * A vessel's plan controls: one that changes the plan, and — for a paid plan — one that ends
 * it or takes that back. Payment state is not surfaced here; that belongs to the billing
 * portal.
 *
 * `subscription` comes from the live query, so these follow the plan as it stands: cancel
 * becomes resume as soon as the refetch lands, and the form opens on the current values.
 *
 * Cancel and resume both need a live Stripe subscription behind the row — the server
 * answers `ErrSubscriptionNotActive` otherwise — which is exactly what `isPaidPlan` means
 * here: not harbor, and not a dead row.
 *
 * Nothing is drawn at all without `EditSubscription`. Every control here writes, and all four
 * endpoints behind them are gated on that one right, so offering them to somebody holding
 * `GetSubscription` alone would promise a change the server would refuse.
 *
 * A vessel whose plan somebody else pays for is the exception, and gets one control of its
 * own — see `subscription.takeover`. The right is not the question there: an owner holds
 * `EditSubscription` over their own vessel whoever bought the plan, and the endpoints behind
 * these buttons check no payer, so every one of them would reach into a subscription that is
 * not theirs.
 *
 * A lifetime plan an admin granted gets no controls at all, only a badge: the server refuses
 * every one of these on it (`SUBSCRIPTION_MANUALLY_GRANTED`).
 */
export function PlanAction(item: ManagedVesselSubscription) {
  const { vessel, subscription, canEdit } = item;
  const [isPlanOpen, setIsPlanOpen] = useState(false);
  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const uncancel = useUncancelSubscriptionMutation();

  const isPaid = isPaidPlan(subscription);
  const isEnding = isPaid && subscription.cancelAtPeriodEnd;
  const isTakeover = canTakeOverPlan(item);

  const resumePlan = () => {
    if (!isPaid) return;
    uncancel.mutate(
      { serviceId: vessel.id, id: subscription.id },
      {
        onError: (error) =>
          toast.error(
            isManuallyGrantedPlan(error)
              ? MANUALLY_GRANTED_MESSAGE
              : `Could not resume the plan for ${vessel.name}. Please try again.`,
          ),
      },
    );
  };

  // Shown to everyone who can see the plan: it is a fact about the plan, not a control.
  if (isLifetimePlan(subscription)) return <LifetimeBadge />;

  if (!canEdit) return null;

  /*
   * A plan on somebody else's card is not this owner's to edit or end — cancelling would
   * close a subscription they are not paying for, and changing it would re-price one. The one
   * thing they can do with it is buy it themselves, which leaves the current plan running
   * untouched until their own payment clears.
   */
  if (isTakeover) {
    return (
      <>
        <TakeOverPlanButton onClick={() => setIsPlanOpen(true)} />
        {isPlanOpen && (
          <VesselPlanModal
            open
            isTakeover
            vessel={vessel}
            subscription={subscription}
            onClose={() => setIsPlanOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isPaid &&
        (isEnding ? (
          <ResumePlanButton onClick={resumePlan} isPending={uncancel.isPending} />
        ) : (
          <CancelPlanButton onClick={() => setIsCancelOpen(true)} />
        ))}

      {/*
        Offered whatever the vessel is on. Harbor comes with the vessel, so there is nothing
        to provision here — every state, including a dead row from an abandoned checkout, is
        moved on from through this one form. Which endpoint it uses is its own decision.
      */}
      <ChangePlanButton onClick={() => setIsPlanOpen(true)} />

      {isPlanOpen && (
        <VesselPlanModal
          open
          vessel={vessel}
          subscription={subscription}
          onClose={() => setIsPlanOpen(false)}
        />
      )}

      {isCancelOpen && isPaid && (
        <CancelPlanModal
          open
          vesselName={vessel.name}
          serviceId={vessel.id}
          subscription={subscription}
          onClose={() => setIsCancelOpen(false)}
        />
      )}
    </>
  );
}
